/**
 * 词汇 C→E 模式的 LLM 简单匹配检查
 * 只输出：mastered / partial / not_mastered
 */

import { LLMOrchestrator } from './orchestrator';
import { buildInvoker } from './providerFactory';
import { callOpenRouter } from './openrouter';
import { readLLMEnabledFlag, readLLMTimeouts, getLLMLogger } from './config';

export type VocabCheckResult = 'mastered' | 'partial' | 'not_mastered';

export interface VocabCheckResponse {
  result: VocabCheckResult;
  provider?: string;
  error?: string;
}

/**
 * 本地精确匹配（忽略大小写和首尾空格）
 */
function localMatch(userAnswer: string, acceptableAnswers: string[]): boolean {
  const normalized = userAnswer.trim().toLowerCase();
  return acceptableAnswers.some(a => a.toLowerCase().trim() === normalized);
}

/**
 * 本地模糊匹配（计算词汇重叠率）
 */
function localFuzzyMatch(userAnswer: string, referenceAnswer: string): VocabCheckResult {
  const userWords = userAnswer.toLowerCase().split(/\s+/).filter(Boolean);
  const refWords = referenceAnswer.toLowerCase().split(/\s+/).filter(Boolean);
  
  if (refWords.length === 0) return 'not_mastered';
  
  const matchCount = userWords.filter(w => refWords.indexOf(w) >= 0).length;
  const ratio = matchCount / refWords.length;
  
  if (ratio >= 0.8) return 'mastered';
  if (ratio >= 0.5) return 'partial';
  return 'not_mastered';
}

/**
 * 检查是否有词汇检查的 LLM 支持
 */
export async function hasVocabCheckSupport(): Promise<boolean> {
  return readLLMEnabledFlag();
}

/**
 * 检查用户答案是否与参考答案匹配
 * 1. 先尝试本地精确匹配
 * 2. 如果不匹配且有 LLM 支持，调用 LLM 做简单匹配判断
 * 3. 无 LLM 时降级到本地模糊匹配
 */
export async function checkVocabAnswer(
  userAnswer: string,
  referenceAnswer: string,
  acceptableAnswers: string[] = []
): Promise<VocabCheckResponse> {
  const allAcceptable = [referenceAnswer, ...acceptableAnswers].filter(Boolean);
  
  // 1. 本地精确匹配
  if (localMatch(userAnswer, allAcceptable)) {
    return { result: 'mastered', provider: 'local-exact' };
  }

  // 2. 检查 LLM 支持
  const hasSupport = await readLLMEnabledFlag();
  if (!hasSupport) {
    // 无 LLM 时，使用本地模糊匹配
    const result = localFuzzyMatch(userAnswer, referenceAnswer);
    return { result, provider: 'local-fuzzy' };
  }

  // 3. 调用 LLM 做简单匹配
  const timeouts = readLLMTimeouts();
  const logger = getLLMLogger();
  
  const prompt = `Compare student answer with reference. Output ONLY one word: mastered, partial, or not_mastered.
Reference: "${referenceAnswer}"
${acceptableAnswers.length > 0 ? `Also acceptable: ${acceptableAnswers.join(', ')}` : ''}
Student: "${userAnswer}"`;

  const orchestrator = new LLMOrchestrator<VocabCheckResponse>(
    [
      buildInvoker('openrouter', async () => {
        const text = await callOpenRouter(prompt);
        const lower = (text ?? '').toLowerCase().trim();
        
        let result: VocabCheckResult = 'not_mastered';
        if (lower.includes('mastered') && !lower.includes('not')) {
          result = 'mastered';
        } else if (lower.includes('partial')) {
          result = 'partial';
        }
        
        return { result, provider: 'openrouter' };
      }),
      buildInvoker('local-fuzzy', async () => {
        const result = localFuzzyMatch(userAnswer, referenceAnswer);
        return { result, provider: 'local-fuzzy' };
      }),
    ],
    { totalTimeoutMs: timeouts.totalTimeoutMs, logger }
  );

  try {
    const res = await orchestrator.run();
    return res.data;
  } catch (err) {
    console.warn('[vocabCheck] all providers failed', err);
    return { 
      result: localFuzzyMatch(userAnswer, referenceAnswer), 
      provider: 'local-fuzzy-fallback',
      error: (err as Error).message 
    };
  }
}
